import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMemberInvoiceDetails } from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_MY_BILLS } from '../../../Routing/routes';
import {
    INVOICE_STATUS_STYLE,
    SYSTEM_HEAD_CODES,
    billStatusLabel,
    daysOverdue,
    fmtAmount,
    fmtCurrency,
    fmtDate,
    fmtPeriod,
    flatLabel,
    humanize,
    isOverdue,
} from './memberConstants';

const Line = ({ label, value, muted }) => (
    <li className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-2 tw-border-b tw-border-gray-50 last:tw-border-b-0">
        <span className={`tw-text-xs ${muted ? 'tw-text-gray-500' : 'tw-text-gray-700'}`}>{label}</span>
        <span className="tw-text-xs tw-font-medium tw-text-gray-800 tw-tabular-nums">{value}</span>
    </li>
);

// One bill, broken down the way a member reads it: what the charges are, what
// the system added on top (arrears, interest, tax, rounding), and what is still
// owed. Asking for a bill on someone else's flat comes back as a 404 from the
// member scope, which lands in the "not available" state below.
const MyBillDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { invoiceDetailsData: invoice, invoiceDetailsLoading } =
        useSelector((state) => state.MemberPortalReducer || {});

    useEffect(() => {
        if (id) dispatch(getMemberInvoiceDetails({ id, societyId }));
    }, [dispatch, id, societyId]);

    const items = useMemo(() => (Array.isArray(invoice?.items) ? invoice.items : []), [invoice]);

    // The invoice engine appends its own heads (tax, arrears, interest, round
    // off). Splitting them out keeps the charge list readable.
    const { chargeItems, arrearItems, taxItems, otherItems } = useMemo(() => {
        const code = (i) => i.tariffHead?.head_code;
        return {
            chargeItems: items.filter((i) => !SYSTEM_HEAD_CODES.includes(code(i))),
            arrearItems: items.filter((i) => ['PAR', 'DNR', 'INT'].includes(code(i))),
            taxItems: items.filter((i) => ['CGST', 'SGST', 'IGST'].includes(code(i))),
            otherItems: items.filter((i) => ['RND', 'ADJ'].includes(code(i))),
        };
    }, [items]);

    const amountOf = (item) => item.system_amount ?? item.base_amount ?? 0;

    if (invoiceDetailsLoading) {
        return (
            <div className="tw-p-5 tw-min-h-screen tw-flex tw-justify-center tw-items-start">
                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-sky-500 tw-mt-24" />
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="tw-p-5 tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50">
                <div className="tw-max-w-md tw-mx-auto tw-mt-20 tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-8 tw-text-center">
                    <Icon icon="mdi:file-remove-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                    <h2 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-mt-3">This bill isn't available</h2>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">
                        It may have been removed, or it belongs to a flat that isn't yours.
                    </p>
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILLS)}
                        className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700"
                    >
                        <Icon icon="mdi:arrow-left" /> Back to my bills
                    </button>
                </div>
            </div>
        );
    }

    const label = billStatusLabel(invoice);
    const overdue = isOverdue(invoice);
    const balance = parseFloat(invoice.balance_due || 0);

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-max-w-4xl tw-mx-auto tw-space-y-4">

                <button
                    onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_MY_BILLS)}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-gray-600 hover:tw-text-sky-700"
                >
                    <Icon icon="mdi:arrow-left" /> My bills
                </button>

                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-font-mono">
                                {invoice.document_number || '-'}
                            </h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                {fmtPeriod(invoice.period_start, invoice.period_end)}
                                {invoice.flat ? ` · ${flatLabel(invoice.flat)}` : ''}
                            </p>
                        </div>
                        <span className={`tw-inline-flex tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-semibold ${INVOICE_STATUS_STYLE[label] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
                            {overdue ? `Overdue by ${daysOverdue(invoice)} days` : humanize(label)}
                        </span>
                    </div>

                    <div className="tw-p-4 tw-grid tw-grid-cols-1 lg:tw-grid-cols-5 tw-gap-4">

                        {/* Breakdown */}
                        <div className="lg:tw-col-span-3">
                            <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold tw-mb-1">
                                Charges
                            </p>
                            <ul className="tw-list-none tw-p-0 tw-m-0">
                                {chargeItems.length === 0 && (
                                    <Line label="No itemised charges on this bill" value="-" muted />
                                )}
                                {chargeItems.map((item) => (
                                    <Line
                                        key={item.invoice_item_id || item.tariffHead?.head_code}
                                        label={item.tariffHead?.head_name || item.description || 'Charge'}
                                        value={fmtAmount(amountOf(item))}
                                    />
                                ))}

                                {arrearItems.map((item) => (
                                    <Line
                                        key={item.invoice_item_id || `arr-${item.tariffHead?.head_code}`}
                                        label={item.tariffHead?.head_name || 'Arrears'}
                                        value={fmtAmount(amountOf(item))}
                                        muted
                                    />
                                ))}
                                {taxItems.map((item) => (
                                    <Line
                                        key={item.invoice_item_id || `tax-${item.tariffHead?.head_code}`}
                                        label={item.tariffHead?.head_name || 'Tax'}
                                        value={fmtAmount(amountOf(item))}
                                        muted
                                    />
                                ))}
                                {otherItems.map((item) => (
                                    <Line
                                        key={item.invoice_item_id || `oth-${item.tariffHead?.head_code}`}
                                        label={item.tariffHead?.head_name || 'Adjustment'}
                                        value={fmtAmount(amountOf(item))}
                                        muted
                                    />
                                ))}
                            </ul>

                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mt-3 tw-pt-3 tw-border-t-2 tw-border-gray-800">
                                <span className="tw-text-sm tw-font-bold tw-text-gray-800">Net payable</span>
                                <span className="tw-text-sm tw-font-bold tw-text-gray-800 tw-tabular-nums">
                                    {fmtCurrency(invoice.net_payable)}
                                </span>
                            </div>
                        </div>

                        {/* Payment summary */}
                        <div className="lg:tw-col-span-2">
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-2">
                                <div className="tw-flex tw-justify-between tw-text-xs">
                                    <span className="tw-text-gray-500">Bill date</span>
                                    <span className="tw-text-gray-800 tw-font-medium">{fmtDate(invoice.invoice_date)}</span>
                                </div>
                                <div className="tw-flex tw-justify-between tw-text-xs">
                                    <span className="tw-text-gray-500">Due date</span>
                                    <span className={`tw-font-medium ${overdue ? 'tw-text-rose-600' : 'tw-text-gray-800'}`}>
                                        {fmtDate(invoice.due_date)}
                                    </span>
                                </div>
                                <div className="tw-flex tw-justify-between tw-text-xs">
                                    <span className="tw-text-gray-500">Paid so far</span>
                                    <span className="tw-text-gray-800 tw-font-medium">{fmtCurrency(invoice.amount_paid)}</span>
                                </div>
                                <div className="tw-flex tw-justify-between tw-text-sm tw-pt-2 tw-border-t tw-border-gray-200">
                                    <span className="tw-font-bold tw-text-gray-800">Balance due</span>
                                    <span className={`tw-font-bold ${balance > 0 ? 'tw-text-rose-600' : 'tw-text-emerald-600'}`}>
                                        {fmtCurrency(balance)}
                                    </span>
                                </div>

                                {balance > 0 ? (
                                    <p className="tw-text-[11px] tw-text-gray-600 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-p-2.5 tw-mt-1">
                                        Pay at the society office or by NEFT/UPI to the society account. Your receipt
                                        appears under <span className="tw-font-semibold">My Payments</span> once the
                                        office records it.
                                    </p>
                                ) : (
                                    <p className="tw-text-[11px] tw-text-emerald-700 tw-bg-emerald-50 tw-rounded-md tw-p-2.5 tw-mt-1">
                                        This bill is fully settled. Nothing to pay.
                                    </p>
                                )}

                                <button
                                    onClick={() => window.print()}
                                    className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-border-sky-400 hover:tw-text-sky-700"
                                >
                                    <Icon icon="mdi:printer-outline" /> Print / save as PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyBillDetail;
